import {
  type AudioClip,
  audioClipEnd,
  type ClipLayout,
  captionPageAt,
  captionText,
  clipCutout,
  clone,
  cutoutTrack,
  DEFAULT_TRANSITION_MS,
  elementLabel,
  formatTimecode,
  groupTrackNumbers,
  hiddenTracks,
  insertionIndexAt,
  insertTrack,
  layoutClips,
  MIN_CLIP_MS,
  mainPersonClips,
  moveClip,
  moveClipToTrack,
  moveElementsToTrack,
  moveOverlayToMain,
  NO_SAFE_ZONES_HINT,
  normalizeVideoLayers,
  type OverlayClip,
  overlayClipEnd,
  overlayClips,
  personTrack,
  placeOverlayClip,
  resolveElementTracks,
  type SceneDocument,
  type SceneElement,
  setElementTime,
  shiftTracksInTime,
  snapTime,
  snapToFrame,
  TRANSITION_KINDS,
  TRANSITION_LABELS,
  type TrackGroup,
  type TrackInfo,
  timelineSnapPoints,
  trackCount,
  trackEntries,
  trackInfo,
  trimClip,
  trimOverlayClip,
  type VideoClip,
  videoDuration,
  videoSafeZones,
} from "@cva/shared";
import { produce } from "immer";
import {
  AlertTriangle,
  AudioLines,
  Blend,
  Captions,
  ChartColumn,
  ChevronDown,
  ChevronRight,
  Copy,
  Crop,
  Eye,
  EyeOff,
  Film,
  FolderOpen,
  GripVertical,
  Image as ImageIcon,
  Layers,
  Loader2,
  Maximize2,
  Minus,
  Music,
  Pause,
  PersonStanding,
  Play,
  Plus,
  QrCode,
  ScanLine,
  Scissors,
  Shapes,
  SkipBack,
  SkipForward,
  Smile,
  Trash2,
  Type,
  VolumeX,
  Wand2,
} from "lucide-react";
import {
  Fragment,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import type { AssetInfo } from "../commands";
import { useEnv } from "../env";
import { PeerClipMark, PeerPlayheads, usePeerOnClip } from "../LivePeers";
import { useLeftNav } from "../left-nav";
import { type EditorStore, useEditor, useEditorApi } from "../store";
import { IconButton, Popover, PopoverContent, PopoverTrigger, Slider, Tip } from "../ui";
import { openCaptions, TranscribingLabel } from "./Captions";
import {
  addClipFromAsset,
  addOverlayFromAsset,
  applyTransition,
  applyTransitionEverywhere,
  deleteSelectedClip,
  duplicateSelectedClip,
  removeSilences,
  setCaptionsHidden,
  splitAtPlayhead,
} from "./commands";
import { useCaptions, useWaveform } from "./data";
import { useVideo, useVideoApi, type VideoStore } from "./store";
import { menuTargetOf, TimelineMenu, type TimelineMenuState } from "./TimelineMenu";
import {
  addTrack,
  moveTrackRows,
  renameGroup,
  setGroupCollapsed,
  setTrackName,
  toggleGroupHidden,
  toggleTrackHidden,
} from "./track-commands";

const PAD = 16;
const RULER = 24;
/** Width of the track headers on the left. */
const HEADER_WIDTH = 168;
const CAPTIONS_ROW = 28;
const VIDEO_ROW = 64;
/** A lane of a track with video clips. */
const CLIP_LANE = 44;
/** A lane of a track with only titles and graphics. */
const ELEMENT_LANE = 26;
/** Drop zone for a new track on top. */
const NEW_TRACK_ROW = 30;
const GROUP_ROW = 24;
/** Per track in a folded group: thin bars, the whole group in view. */
const SUMMARY_LANE = 6;
const PERSON_ROW = 22;
const AUDIO_LANE = 36;
const SNAP_PX = 7;
/** Near the edge between two rows: a new track goes in there. */
const EDGE_PX = 6;

/** Where something dragged up or down lands. */
type DropTarget =
  | { type: "track"; track: number }
  /** A new track at this position (the tracks from there move up). */
  | { type: "insert"; at: number }
  | { type: "main" };

type Gesture =
  | { type: "scrub" }
  | {
      type: "clip-move";
      clipId: string;
      startX: number;
      startY: number;
      entry: ClipLayout;
      points: number[];
      moved: boolean;
      insertAt: number;
      /** Dragged up onto a track: becomes a clip of that track. */
      toTrack: { target: DropTarget; start: number } | null;
    }
  | {
      type: "clip-trim";
      clipId: string;
      edge: "in" | "out";
      startX: number;
      entry: ClipLayout;
      maxSource: number;
      points: number[];
    }
  | {
      type: "overlay";
      id: string;
      mode: "move" | "start" | "end";
      startX: number;
      startY: number;
      start: number;
      end: number;
      points: number[];
      moved: boolean;
      /** Up or down onto another track (applied when dropped). */
      drop: DropTarget | null;
      /** The element's track when the drag started. */
      own: number | undefined;
    }
  | {
      type: "audio";
      id: string;
      mode: "move" | "start" | "end";
      startX: number;
      clip: AudioClip;
      max: number;
      points: number[];
      moved: boolean;
    }
  | {
      type: "track";
      id: string;
      mode: "move" | "start" | "end";
      startX: number;
      startY: number;
      clip: OverlayClip;
      max: number;
      points: number[];
      moved: boolean;
      /** Up or down onto another track (applied when dropped). */
      drop: DropTarget | null;
      /** Dragged down onto the main track: goes in at this index. */
      toMain: number | null;
    }
  | {
      /** A track header (or a group's) dragged up or down: reorders the tracks. */
      type: "rows";
      first: number;
      count: number;
      startY: number;
      moved: boolean;
      before: number | null;
    }
  | {
      /** A folded group dragged sideways: everything on its tracks moves in time. */
      type: "group-shift";
      tracks: number[];
      startX: number;
      moved: boolean;
      points: number[];
      earliest: number;
    };

interface LaneItem<T> {
  item: T;
  lane: number;
}

type Row =
  | { kind: "new"; top: number; height: number }
  | { kind: "captions-cta"; top: number; height: number }
  | {
      kind: "group";
      top: number;
      height: number;
      group: TrackGroup;
      tracks: number[];
      hidden: boolean;
    }
  | { kind: "summary"; top: number; height: number; group: TrackGroup; tracks: number[] }
  | {
      kind: "track";
      top: number;
      height: number;
      track: number;
      info: TrackInfo | null;
      laneHeight: number;
      clips: LaneItem<OverlayClip>[];
      elements: LaneItem<SceneElement>[];
      captions: boolean;
      grouped: boolean;
      /** Below the person track: what lies here is behind the cut-out person. */
      behindPerson: boolean;
    }
  /** The person track: where the cut-out person lies in the stack (`split`: its clips' track). */
  | {
      kind: "person";
      top: number;
      height: number;
      track: number;
      split: number;
      info: TrackInfo | null;
      grouped: boolean;
    }
  | { kind: "main"; top: number; height: number }
  | { kind: "music"; top: number; height: number };

/** A row before it gets its place. */
type RowInput = Row extends infer R ? (R extends Row ? Omit<R, "top"> : never) : never;

/** Greedy lanes for items with time ranges (no overlap within a lane). */
function lanes<T>(items: T[], range: (item: T) => { start: number; end: number }): Map<T, number> {
  const ends: number[] = [];
  const out = new Map<T, number>();
  const sorted = [...items].sort((a, b) => range(a).start - range(b).start);
  for (const item of sorted) {
    const { start, end } = range(item);
    let lane = ends.findIndex((laneEnd) => laneEnd <= start + 1);
    if (lane < 0) {
      lane = ends.length;
      ends.push(end);
    } else ends[lane] = end;
    out.set(item, lane);
  }
  return out;
}

/** Default name of a track (the main track is "Spur 1"). */
function trackName(track: number, info: TrackInfo | null, captions: boolean): string {
  if (info?.name) return info.name;
  return captions ? "Untertitel" : `Spur ${track + 1}`;
}

/**
 * The rows of the timeline from top to bottom, like the layers: the highest
 * track first, down to the main track, then the music.
 */
function buildRows(
  doc: SceneDocument,
  duration: number,
  audioLanes: number,
  hasCaptions: boolean,
): { rows: Row[]; height: number } {
  const count = trackCount(doc);
  const resolved = resolveElementTracks(doc);
  const split = cutoutTrack(doc.timeline);
  const clipsOn = new Map<number, OverlayClip[]>();
  for (const clip of overlayClips(doc.timeline)) {
    clipsOn.set(clip.track, [...(clipsOn.get(clip.track) ?? []), clip]);
  }
  const person = split === null ? null : personTrack(doc);
  const elementsOn = new Map<number, SceneElement[]>();
  for (const id of doc.pages[0]?.children ?? []) {
    const element = doc.elements[id];
    const track = resolved.get(id);
    if (!element || track === undefined) continue;
    elementsOn.set(track, [...(elementsOn.get(track) ?? []), element]);
  }
  const rangeOf = (element: SceneElement) => element.time ?? { start: 0, end: duration };
  const rows: Row[] = [];
  let y = RULER;
  const push = (row: RowInput) => {
    rows.push({ ...row, top: y } as Row);
    y += row.height;
  };
  push({ kind: "new", height: NEW_TRACK_ROW });
  if (!hasCaptions) push({ kind: "captions-cta", height: CAPTIONS_ROW });
  const groups = new Map((doc.timeline?.trackGroups ?? []).map((group) => [group.id, group]));
  let track = count;
  while (track >= 1) {
    const info = trackInfo(doc, track);
    const group = info?.group ? groups.get(info.group) : undefined;
    if (group && trackInfo(doc, track + 1)?.group !== group.id) {
      const members = groupTrackNumbers(doc, group.id);
      push({
        kind: "group",
        height: GROUP_ROW,
        group,
        tracks: members,
        hidden: members.every((member) => trackInfo(doc, member)?.hidden),
      });
      if (group.collapsed) {
        push({
          kind: "summary",
          height: Math.max(20, members.length * SUMMARY_LANE + 8),
          group,
          tracks: members,
        });
        track = Math.min(...members) - 1;
        continue;
      }
    }
    if (track === person && split !== null) {
      push({ kind: "person", height: PERSON_ROW, track, split, info, grouped: Boolean(group) });
      track--;
      continue;
    }
    const clips = clipsOn.get(track) ?? [];
    const elements = elementsOn.get(track) ?? [];
    const captions = elements.some((element) => element.type === "captions");
    type Item = { clip?: OverlayClip; element?: SceneElement; start: number; end: number };
    const items: Item[] = [
      ...clips.map((clip) => ({ clip, start: clip.start, end: overlayClipEnd(clip) })),
      ...elements
        .filter((element) => element.type !== "captions")
        .map((element) => ({ element, ...rangeOf(element) })),
    ];
    const map = lanes(items, (item) => item);
    const laneCount = Math.max(1, ...[...map.values()].map((lane) => lane + 1));
    const laneHeight = clips.length > 0 ? CLIP_LANE : ELEMENT_LANE;
    push({
      kind: "track",
      height: captions ? CAPTIONS_ROW : laneCount * laneHeight,
      track,
      info,
      laneHeight,
      clips: items.flatMap((item) =>
        item.clip ? [{ item: item.clip, lane: map.get(item) ?? 0 }] : [],
      ),
      elements: items.flatMap((item) =>
        item.element ? [{ item: item.element, lane: map.get(item) ?? 0 }] : [],
      ),
      captions,
      grouped: Boolean(group),
      behindPerson: person !== null && track < person,
    });
    track--;
  }
  push({ kind: "main", height: VIDEO_ROW });
  push({ kind: "music", height: audioLanes * AUDIO_LANE });
  return { rows, height: y + 8 };
}

/** The media library, filtered to music and voice-overs. */
function openAudioLibrary() {
  useLeftNav.getState().open("media", { media: "audio" });
}

function tickStep(pxPerMs: number): number {
  const steps = [100, 250, 500, 1000, 2000, 5000, 10_000, 15_000, 30_000, 60_000, 120_000];
  return steps.find((step) => step * pxPerMs >= 64) ?? 300_000;
}

function elementIcon(element: SceneElement): ReactNode {
  if (element.type === "text") return <Type />;
  if (element.type === "image" || element.type === "svg") return <ImageIcon />;
  if (element.type === "qr") return <QrCode />;
  if (element.type === "icon") return <Smile />;
  if (element.type === "chart") return <ChartColumn />;
  if (element.type === "captions") return <Captions />;
  return <Shapes />;
}

/** Copies the times of the elements (and the upper track clips) from `base` back into `draft`. */
function restoreTimes(draft: SceneDocument, base: SceneDocument) {
  if (base.timeline?.overlays && draft.timeline) {
    draft.timeline.overlays = clone(base.timeline.overlays);
  }
  for (const [id, element] of Object.entries(base.elements)) {
    const target = draft.elements[id];
    if (!target) continue;
    if (target.time?.start === element.time?.start && target.time?.end === element.time?.end) {
      continue;
    }
    if (element.time) target.time = { ...element.time };
    else delete target.time;
  }
}

export function Timeline() {
  const store = useEditorApi();
  const video = useVideoApi()!;
  const env = useEnv();
  const doc = useEditor((s) => s.doc);
  const readOnly = useEditor((s) => s.readOnly);
  const selection = useEditor((s) => s.selection);
  const pxPerSecond = useVideo((s) => s.pxPerSecond);
  const selectedClip = useVideo((s) => s.selectedClip);
  const { words, captionsElement, infos } = useCaptions();
  const scroller = useRef<HTMLDivElement>(null);
  const labels = useRef<HTMLDivElement>(null);
  /** The headers' content: moved with the rows (scrollTop could not reach the rows' last pixels). */
  const labelsContent = useRef<HTMLDivElement>(null);
  const gesture = useRef<Gesture | null>(null);
  const [dragGhost, setDragGhost] = useState<{ x: number; insertX: number } | null>(null);
  // Where a clip dragged between the main track and the tracks above would land.
  const [trackGhost, setTrackGhost] = useState<{ start: number; length: number } | null>(null);
  /** Where a drag up or down lands: a row, or a line for a new track / a new place. */
  const [indicator, setIndicator] = useState<
    | { type: "row"; top: number; height: number }
    | { type: "line"; y: number; label?: string }
    | null
  >(null);
  const [selectedTracks, setSelectedTracks] = useState<number[]>([]);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [width, setWidth] = useState(800);
  const [menu, setMenu] = useState<TimelineMenuState | null>(null);
  const pxPerMs = pxPerSecond / 1000;
  const duration = videoDuration(doc);
  const layout = useMemo(() => layoutClips(doc.timeline), [doc.timeline]);
  const fps = doc.timeline?.fps ?? 30;
  // Rows as the next change writes them (older documents: tracks, person track).
  const view = useMemo(
    () => produce(doc, (draft) => normalizeVideoLayers(draft as SceneDocument)),
    [doc],
  );
  const count = trackCount(view);
  const hidden = hiddenTracks(view);

  useLayoutEffect(() => {
    const element = scroller.current;
    if (!element) return;
    const observer = new ResizeObserver(() => setWidth(element.clientWidth));
    observer.observe(element);
    setWidth(element.clientWidth);
    return () => observer.disconnect();
  }, []);

  // Fit the whole video when the editor opens.
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current || width < 100) return;
    fitted.current = true;
    video.getState().setZoom(((width - PAD * 2) / Math.max(duration, 5000)) * 1000);
  }, [width, duration, video]);

  const audioClips = doc.timeline?.audio ?? [];
  const audioLanesMap = useMemo(
    () =>
      lanes(
        [...audioClips].sort((a, b) => a.start - b.start),
        (clip) => ({ start: clip.start, end: audioClipEnd(clip) }),
      ),
    [audioClips],
  );
  const audioLanes = Math.max(1, ...[...audioLanesMap.values()].map((lane) => lane + 1));

  const { rows, height: totalHeight } = useMemo(
    () => buildRows(view, duration, audioLanes, Boolean(captionsElement)),
    [view, duration, audioLanes, captionsElement],
  );
  const mainRow = rows.find((row) => row.kind === "main")!;
  const musicRow = rows.find((row) => row.kind === "music")!;
  const newRow = rows.find((row) => row.kind === "new")!;
  const trackRows = rows.filter(
    (row): row is Extract<Row, { kind: "track" }> => row.kind === "track",
  );
  /** The row of a track (or of the person track). */
  const rowOfTrack = (track: number) =>
    rows.find(
      (row): row is Extract<Row, { kind: "track" | "person" }> =>
        (row.kind === "track" || row.kind === "person") && row.track === track,
    );
  const person = rows.find((row) => row.kind === "person");

  const contentWidth = Math.max(width, (duration + 4000) * pxPerMs + PAD * 2);

  /** y of a pointer inside the rows (content coordinates). */
  const contentY = (clientY: number) => {
    const rect = scroller.current!.getBoundingClientRect();
    return clientY - rect.top + scroller.current!.scrollTop;
  };
  const rowAt = (clientY: number) => {
    const y = contentY(clientY);
    return rows.find((row) => y >= row.top && y < row.top + row.height) ?? null;
  };
  /** Where something dragged to `clientY` lands (null: nowhere). */
  const dropTargetAt = (clientY: number): DropTarget | null => {
    const y = contentY(clientY);
    const row = rowAt(clientY);
    if (!row) return y < RULER + NEW_TRACK_ROW ? { type: "insert", at: count + 1 } : null;
    const top = y - row.top < EDGE_PX;
    const bottom = row.top + row.height - y < EDGE_PX;
    switch (row.kind) {
      case "new":
      case "captions-cta":
        return { type: "insert", at: count + 1 };
      case "track":
        if (top) return { type: "insert", at: row.track + 1 };
        if (bottom) return { type: "insert", at: row.track };
        return { type: "track", track: row.track };
      case "group":
      case "summary":
        return { type: "insert", at: Math.max(...row.tracks) + 1 };
      case "person":
        // Onto the person: right below it, i.e. behind the person.
        return top ? { type: "insert", at: row.track + 1 } : { type: "insert", at: row.track };
      case "main":
        return top ? { type: "insert", at: 1 } : { type: "main" };
      default:
        return null;
    }
  };
  /** The marker of a drop target. */
  const showTarget = (target: DropTarget | null, own?: number) => {
    if (!target || (target.type === "track" && target.track === own)) return setIndicator(null);
    if (target.type === "track") {
      const row = rowOfTrack(target.track);
      return setIndicator(row ? { type: "row", top: row.top, height: row.height } : null);
    }
    if (target.type === "main") return setIndicator(null);
    // A line where the new track goes: above the row of the track now at `at`.
    const above = rowOfTrack(target.at);
    const below = rowOfTrack(target.at - 1);
    const y = below ? below.top : above ? above.top + above.height : newRow.top + newRow.height;
    const behind = person?.kind === "person" && target.at <= person.track;
    setIndicator({ type: "line", y, label: behind ? "Neue Spur hinter der Person" : "Neue Spur" });
  };
  /** Gap between track rows under `clientY` for reordering: the block goes below track `before`. */
  const rowGapAt = (clientY: number): { before: number; y: number } => {
    const y = contentY(clientY);
    let best = { before: count + 1, y: newRow.top + newRow.height, distance: Infinity };
    for (const row of rows) {
      const tracks =
        row.kind === "track" || row.kind === "person"
          ? [row.track]
          : row.kind === "summary" || row.kind === "group"
            ? row.tracks
            : null;
      if (!tracks) continue;
      const high = Math.max(...tracks);
      const low = Math.min(...tracks);
      const gaps =
        row.kind === "group"
          ? [{ before: high + 1, y: row.top }]
          : [
              { before: high + 1, y: row.top },
              { before: low, y: row.top + row.height },
            ];
      for (const gap of gaps) {
        const distance = Math.abs(gap.y - y);
        if (distance < best.distance) best = { ...gap, distance };
      }
    }
    return best;
  };

  /** Insertion point of the main track at a time (for the drop marker). */
  const insertMarker = (t: number) => {
    const index = insertionIndexAt(store.getState().doc.timeline, t);
    const entry = layout[index];
    return { index, x: entry ? xOf(entry.start) : xOf(layout[layout.length - 1]?.end ?? 0) };
  };

  const timeAt = (clientX: number) => {
    const rect = scroller.current!.getBoundingClientRect();
    return (clientX - rect.left + scroller.current!.scrollLeft - PAD) / pxPerMs;
  };
  const xOf = (ms: number) => PAD + ms * pxPerMs;
  const snapPoints = (exclude?: string) =>
    timelineSnapPoints(
      store.getState().doc,
      [video.getState().time, ...words.flatMap((word) => [word.start, word.end])],
      exclude,
    );
  const threshold = SNAP_PX / pxPerMs;

  const seekTo = (clientX: number) => {
    const t = Math.max(0, Math.min(duration, timeAt(clientX)));
    video.getState().seek(snapToFrame(t, fps));
  };

  /** Applies where elements were dropped (inside the running gesture: one undo step). */
  const dropElements = (
    ids: readonly string[],
    target: DropTarget | null,
    own: number | undefined,
  ) => {
    const top = new Set(store.getState().doc.pages[0]?.children ?? []);
    const moving = ids.filter((id) => top.has(id));
    if (moving.length === 0) return;
    store.getState().update((draft) => {
      if (target?.type === "insert") {
        const at = insertTrack(draft, target.at, { keep: false });
        moveElementsToTrack(draft, moving, at);
        return;
      }
      const track = target?.type === "track" ? target.track : own;
      if (track) moveElementsToTrack(draft, moving, track);
    });
  };

  const onPointerMove = (event: ReactPointerEvent) => {
    const g = gesture.current;
    if (!g) return;
    const state = store.getState();
    if (g.type === "scrub") return seekTo(event.clientX);
    if (g.type === "rows") {
      if (!g.moved && Math.abs(event.clientY - g.startY) < 4) return;
      g.moved = true;
      const gap = rowGapAt(event.clientY);
      g.before = gap.before;
      setIndicator({ type: "line", y: gap.y });
      return;
    }
    const dx = event.clientX - g.startX;
    const dms = dx / pxPerMs;
    if (g.type === "group-shift") {
      if (!g.moved && Math.abs(dx) < 3) return;
      if (!g.moved) {
        g.moved = true;
        state.beginGesture();
      }
      const wanted = snapTime(g.earliest + dms, g.points, threshold);
      const base = store.getState().gestureBase ?? state.doc;
      state.update((draft) => {
        restoreTimes(draft, base);
        shiftTracksInTime(draft, g.tracks, wanted - g.earliest);
      });
      return;
    }
    if (g.type === "clip-move") {
      if (!g.moved && Math.abs(dx) < 4 && Math.abs(event.clientY - g.startY) < 8) return;
      if (!g.moved) g.moved = true;
      const target = dropTargetAt(event.clientY);
      if (target && target.type !== "main") {
        // Up onto a track: the clip keeps its length and follows the pointer in time.
        const length = g.entry.end - g.entry.start;
        let start = snapTime(g.entry.start + dms, g.points, threshold);
        const end = snapTime(g.entry.end + dms, g.points, threshold);
        if (Math.abs(end - (g.entry.end + dms)) < Math.abs(start - (g.entry.start + dms))) {
          start = end - length;
        }
        g.toTrack = { target, start: Math.max(0, Math.round(start)) };
        setDragGhost(null);
        setTrackGhost({ start: g.toTrack.start, length });
        showTarget(target);
        return;
      }
      g.toTrack = null;
      setTrackGhost(null);
      setIndicator(null);
      const marker = insertMarker(timeAt(event.clientX));
      g.insertAt = marker.index;
      const rect = scroller.current!.getBoundingClientRect();
      setDragGhost({
        x: event.clientX - rect.left + scroller.current!.scrollLeft,
        insertX: marker.x,
      });
      return;
    }
    if (g.type === "clip-trim") {
      if (!state.gestureBase) state.beginGesture();
      const base = store.getState().gestureBase!;
      const { entry } = g;
      const clip = entry.clip;
      // Always trim the state from the start of the drag: the ripple of overlays and
      // music would otherwise depend on the path of the pointer.
      const trimFromBase = (edge: "in" | "out", source: number) =>
        state.update((draft) => {
          if (base.timeline) draft.timeline = clone(base.timeline);
          for (const [id, element] of Object.entries(base.elements)) {
            const target = draft.elements[id];
            const same =
              target?.time?.start === element.time?.start &&
              target?.time?.end === element.time?.end;
            if (!target || same) continue;
            if (element.time) target.time = { ...element.time };
            else delete target.time;
          }
          trimClip(draft, clip.id, edge, source, g.maxSource);
        });
      if (g.edge === "out") {
        const wanted = snapTime(entry.end + dms, g.points, threshold);
        const source = clip.sourceIn + (wanted - entry.start) * clip.speed;
        trimFromBase("out", source);
        const updated = layoutClips(store.getState().doc.timeline).find(
          (item) => item.clip.id === clip.id,
        );
        if (updated) video.getState().seek(Math.max(updated.start, updated.end - 1000 / fps));
      } else {
        const wanted = snapTime(entry.start + dms, g.points, threshold);
        const source = clip.sourceIn + (wanted - entry.start) * clip.speed;
        trimFromBase("in", source);
        video.getState().seek(entry.start);
      }
      return;
    }
    if (g.type === "overlay") {
      const dy = event.clientY - g.startY;
      if (!g.moved && Math.abs(dx) < 3 && Math.abs(dy) < 6) return;
      if (!g.moved) {
        g.moved = true;
        state.beginGesture();
      }
      if (g.mode === "move") {
        const target = dropTargetAt(event.clientY);
        const same =
          (target?.type === "track" && target.track === g.own) || target?.type === "main";
        g.drop = same ? null : target;
        showTarget(g.drop, g.own);
      }
      let start = g.start;
      let end = g.end;
      if (g.mode === "move") {
        const length = g.end - g.start;
        const snappedStart = snapTime(g.start + dms, g.points, threshold);
        const snappedEnd = snapTime(g.end + dms, g.points, threshold);
        start =
          Math.abs(snappedStart - (g.start + dms)) <= Math.abs(snappedEnd - (g.end + dms))
            ? snappedStart
            : snappedEnd - length;
        start = Math.max(0, start);
        end = start + length;
      } else if (g.mode === "start") {
        start = Math.max(
          0,
          Math.min(g.end - MIN_CLIP_MS, snapTime(g.start + dms, g.points, threshold)),
        );
      } else {
        end = Math.max(g.start + MIN_CLIP_MS, snapTime(g.end + dms, g.points, threshold));
      }
      // A whole-video element moved only up or down keeps "the whole video".
      if (g.mode === "move" && Math.abs(dx) < 3 && !state.gestureBase?.elements[g.id]?.time) return;
      state.update((draft) => {
        // Groups take their content along; times come from before the drag, so nothing adds up.
        const base = state.gestureBase ?? draft;
        if (g.mode !== "move") {
          setElementTime(draft, g.id, { start, end }, base, "resize");
          return;
        }
        // The other selected (unlocked) elements move by the same amount; the earliest
        // stops at 0, so their distances stay.
        const others = state.selection.filter(
          (id) => id !== g.id && base.elements[id]?.time && !base.elements[id]?.locked,
        );
        const earliest = Math.min(g.start, ...others.map((id) => base.elements[id]!.time!.start));
        const delta = Math.max(start - g.start, -earliest);
        setElementTime(
          draft,
          g.id,
          { start: g.start + delta, end: g.start + delta + (end - start) },
          base,
          "move",
        );
        for (const id of others) {
          const range = base.elements[id]!.time!;
          const from = range.start + delta;
          setElementTime(
            draft,
            id,
            { start: from, end: from + range.end - range.start },
            base,
            "move",
          );
        }
      });
      return;
    }
    if (g.type === "track") {
      if (!g.moved && Math.abs(dx) < 3 && Math.abs(event.clientY - g.startY) < 6) return;
      if (!g.moved) {
        g.moved = true;
        state.beginGesture();
      }
      const { clip } = g;
      if (g.mode === "move") {
        const target = dropTargetAt(event.clientY);
        if (target?.type === "main") {
          // Down onto the main track: it stays where it was until it is dropped.
          const marker = insertMarker(timeAt(event.clientX));
          g.toMain = marker.index;
          g.drop = null;
          setIndicator(null);
          setDragGhost({ x: 0, insertX: marker.x });
          return;
        }
        g.toMain = null;
        setDragGhost(null);
        g.drop =
          target && !(target.type === "track" && target.track === clip.track) ? target : null;
        showTarget(g.drop, clip.track);
      }
      state.update((draft) => {
        const target = draft.timeline?.overlays?.find((item) => item.id === g.id);
        if (!target) return;
        Object.assign(target, {
          start: clip.start,
          sourceIn: clip.sourceIn,
          sourceOut: clip.sourceOut,
        });
        if (g.mode === "move") {
          const length = overlayClipEnd(clip) - clip.start;
          let start = snapTime(clip.start + dms, g.points, threshold);
          const endSnap = snapTime(clip.start + length + dms, g.points, threshold);
          if (
            Math.abs(endSnap - (clip.start + length + dms)) < Math.abs(start - (clip.start + dms))
          ) {
            start = endSnap - length;
          }
          target.start = Math.max(0, Math.round(start));
        } else if (g.mode === "start") {
          const wanted = snapTime(clip.start + dms, g.points, threshold);
          trimOverlayClip(draft, g.id, "in", wanted, g.max);
        } else {
          const wanted = snapTime(overlayClipEnd(clip) + dms, g.points, threshold);
          trimOverlayClip(draft, g.id, "out", wanted, g.max);
        }
      });
      return;
    }
    if (g.type === "audio") {
      if (!g.moved && Math.abs(dx) < 3) return;
      if (!g.moved) {
        g.moved = true;
        state.beginGesture();
      }
      const { clip } = g;
      state.update((draft) => {
        const target = draft.timeline?.audio.find((item) => item.id === g.id);
        if (!target) return;
        if (g.mode === "move") {
          const length = clip.sourceOut - clip.sourceIn;
          let start = snapTime(clip.start + dms, g.points, threshold);
          const endSnap = snapTime(clip.start + length + dms, g.points, threshold);
          if (
            Math.abs(endSnap - (clip.start + length + dms)) < Math.abs(start - (clip.start + dms))
          ) {
            start = endSnap - length;
          }
          target.start = Math.max(0, Math.round(start));
        } else if (g.mode === "start") {
          const wanted = snapTime(clip.start + dms, g.points, threshold);
          const shift = Math.max(
            -clip.sourceIn,
            Math.min(clip.sourceOut - clip.sourceIn - MIN_CLIP_MS, wanted - clip.start),
          );
          const clamped = Math.max(-clip.start, shift);
          target.start = Math.round(clip.start + clamped);
          target.sourceIn = Math.round(clip.sourceIn + clamped);
        } else {
          const wanted = snapTime(audioClipEnd(clip) + dms, g.points, threshold);
          target.sourceOut = Math.round(
            Math.min(
              g.max,
              Math.max(clip.sourceIn + MIN_CLIP_MS, clip.sourceIn + wanted - clip.start),
            ),
          );
        }
      });
    }
  };

  const onPointerUp = () => {
    const g = gesture.current;
    gesture.current = null;
    setIndicator(null);
    if (g?.type === "rows") {
      if (g.moved && g.before !== null) moveTrackRows(store, g.first, g.count, g.before);
      return;
    }
    if (g?.type === "clip-move" && g.moved) {
      const target = g.toTrack;
      if (target) {
        let created: string | null = null;
        store.getState().update((draft) => {
          const track =
            target.target.type === "insert"
              ? insertTrack(draft, target.target.at, { keep: false })
              : target.target.type === "track"
                ? target.target.track
                : trackCount(draft) + 1;
          created = moveClipToTrack(draft, g.clipId, target.start, track);
          const placed = draft.timeline?.overlays?.find((item) => item.id === created);
          if (created && placed) placeOverlayClip(draft, created, placed.start, track);
        });
        if (created) video.getState().selectClip({ track: "overlay", id: created });
      } else {
        store.getState().update((draft) => moveClip(draft, g.clipId, g.insertAt));
      }
      setDragGhost(null);
      setTrackGhost(null);
    }
    if (g?.type === "overlay" && g.moved && g.mode === "move") {
      const ids = selection.includes(g.id) ? selection : [g.id];
      // Also when only moved in time: a taken place sends it to a new track above.
      dropElements(ids, g.drop, g.own);
    }
    if (g?.type === "track" && g.moved && g.mode === "move") {
      const index = g.toMain;
      const drop = g.drop;
      let created: string | null = null;
      store.getState().update((draft) => {
        if (index !== null) {
          created = moveOverlayToMain(draft, g.id, index);
          return;
        }
        const target = draft.timeline?.overlays?.find((item) => item.id === g.id);
        if (!target) return;
        const track =
          drop?.type === "insert"
            ? insertTrack(draft, drop.at, { keep: false })
            : drop?.type === "track"
              ? drop.track
              : target.track;
        // A taken place: a new track right above.
        placeOverlayClip(draft, g.id, target.start, track);
      });
      if (created) video.getState().selectClip({ track: "video", id: created });
      setDragGhost(null);
    }
    store.getState().endGesture();
  };

  const startScrub = (event: ReactPointerEvent) => {
    if (event.button !== 0) return;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    gesture.current = { type: "scrub" };
    seekTo(event.clientX);
  };

  const capture = (event: ReactPointerEvent) => {
    event.stopPropagation();
    scroller.current?.setPointerCapture(event.pointerId);
  };

  const selectClip = (track: "video" | "overlay" | "audio", id: string) => {
    store.getState().select([]);
    video.getState().selectClip({ track, id });
  };

  const selectOverlay = (element: SceneElement) => {
    video.getState().selectClip(null);
    store.getState().select([element.id]);
    const t = video.getState().time;
    if (element.time && (t < element.time.start || t >= element.time.end)) {
      video.getState().seek(element.time.start);
    }
  };

  /** Right click: selects what is under the pointer (moves nothing) and opens its menu. */
  const onContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    if (gesture.current) return;
    let target = menuTargetOf(event.target as Element);
    const state = store.getState();
    if (target.kind === "clip") selectClip("video", target.id);
    else if (target.kind === "overlay") selectClip("overlay", target.id);
    else if (target.kind === "audio") selectClip("audio", target.id);
    else if (target.kind === "element" && !state.selection.includes(target.id)) {
      // Without seeking: "An den Abspielkopf" refers to where the playhead is now.
      video.getState().selectClip(null);
      state.select([target.id]);
    } else if (target.kind === "track") {
      const tracks = selectedTracks.includes(target.track) ? selectedTracks : [target.track];
      if (!selectedTracks.includes(target.track)) setSelectedTracks([target.track]);
      target = { ...target, selected: tracks };
    }
    const t = Math.max(0, Math.min(duration, timeAt(event.clientX)));
    setMenu({ x: event.clientX, y: event.clientY, t, target });
  };

  // Drops from the media panels.
  const onDrop = (event: React.DragEvent, track: "video" | "audio" | number) => {
    event.preventDefault();
    if (readOnly) return;
    const t = Math.max(0, timeAt(event.clientX));
    const assetId = event.dataTransfer.getData("application/x-cva-asset");
    if (assetId) {
      const asset = env.assets.get(assetId);
      if (!asset) return;
      if (asset.kind === "audio") addClipFromAsset(store, video, asset, { at: t });
      else if (typeof track === "number")
        addOverlayFromAsset(store, video, asset, {
          at: snapTime(t, snapPoints(), threshold),
          track,
        });
      else if (track === "video" || asset.kind === "video")
        addClipFromAsset(store, video, asset, { index: insertionIndexAt(doc.timeline, t) });
      return;
    }
    const files = [...event.dataTransfer.files];
    if (files.length > 0) {
      env.uploadAndInsert(files, { timeline: { index: insertionIndexAt(doc.timeline, t), at: t } });
    }
  };

  const allowDrop = (event: React.DragEvent) => {
    if (readOnly) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  /** Header click: selects the track (Shift / ⌘: several, to group them). */
  const selectTrack = (event: ReactPointerEvent, track: number) => {
    if (event.shiftKey && selectedTracks.length > 0) {
      const from = Math.min(track, ...selectedTracks);
      const to = Math.max(track, ...selectedTracks);
      setSelectedTracks(Array.from({ length: to - from + 1 }, (_, index) => from + index));
    } else if (event.metaKey || event.ctrlKey) {
      setSelectedTracks((current) =>
        current.includes(track) ? current.filter((item) => item !== track) : [...current, track],
      );
    } else setSelectedTracks([track]);
  };
  /** The grip of a header: drag up or down to move the track (or group). */
  const startRowDrag = (event: ReactPointerEvent, first: number, rowCount: number) => {
    if (event.button !== 0 || readOnly) return;
    event.stopPropagation();
    event.preventDefault();
    labels.current?.setPointerCapture(event.pointerId);
    gesture.current = {
      type: "rows",
      first,
      count: rowCount,
      startY: event.clientY,
      moved: false,
      before: null,
    };
  };

  const elementBar = (
    element: SceneElement,
    rowTop: number,
    lane: number,
    laneHeight: number,
    dimmed: boolean,
  ) => {
    const range = element.time ?? { start: 0, end: duration };
    const selected = selection.includes(element.id);
    const begin = (event: ReactPointerEvent, mode: "move" | "start" | "end") => {
      // The right button opens the context menu (it selects on its own).
      if (event.button !== 0) return event.stopPropagation();
      capture(event);
      // Shift / ⌘: several elements (to group them or move them together).
      if (event.shiftKey || event.metaKey || event.ctrlKey) {
        video.getState().selectClip(null);
        store.getState().select([element.id], "toggle");
        return;
      }
      // A click on one of several selected elements keeps the selection (moves all).
      if (!(mode === "move" && selection.length > 1 && selected)) selectOverlay(element);
      if (readOnly || element.locked) return;
      gesture.current = {
        type: "overlay",
        id: element.id,
        mode,
        startX: event.clientX,
        startY: event.clientY,
        start: range.start,
        // Moving keeps the whole length (a group past the video end keeps its
        // staggered parts); only a resized end stops at the video's end.
        end:
          mode === "move" && element.time
            ? range.end
            : Math.min(range.end, Math.max(duration, range.start + MIN_CLIP_MS)),
        points: snapPoints(element.id),
        moved: false,
        drop: null,
        own: resolveElementTracks(view).get(element.id),
      };
    };
    return (
      <div
        key={element.id}
        data-menu={`element:${element.id}`}
        className={cn(
          "group absolute z-[1] flex items-center gap-1 overflow-hidden rounded-md border px-1.5 text-[11px] [&_svg]:size-3 [&_svg]:shrink-0",
          element.time
            ? "border-sky-300 bg-sky-100 text-sky-900"
            : "border-sky-200 bg-sky-50 text-sky-800/80",
          selected && "ring-2 ring-brand-500",
          (element.hidden || dimmed) && "opacity-40",
        )}
        style={{
          left: xOf(range.start),
          width: Math.max(6, (range.end - range.start) * pxPerMs),
          top: rowTop + lane * laneHeight + 2,
          height: laneHeight - 4,
        }}
        onPointerDown={(event) => begin(event, "move")}
        title={`${elementLabel(element)} · ${formatTimecode(range.start)}–${formatTimecode(range.end)}`}
      >
        {elementIcon(element)}
        <span className="truncate">{elementLabel(element).replace(/[„“]/g, "")}</span>
        {element.animation?.in || element.animation?.out ? <Wand2 /> : null}
        <Handle side="left" onPointerDown={(event) => begin(event, "start")} />
        <Handle side="right" onPointerDown={(event) => begin(event, "end")} />
      </div>
    );
  };

  const overlayItem = (clip: OverlayClip, top: number, height: number, dimmed: boolean) => (
    <TrackItem
      key={clip.id}
      clip={clip}
      asset={env.assets.get(clip.assetId)}
      pxPerMs={pxPerMs}
      left={xOf(clip.start)}
      top={top}
      height={height}
      dimmed={dimmed}
      selected={selectedClip?.track === "overlay" && selectedClip.id === clip.id}
      onPointerDown={(event, mode) => {
        if (event.button !== 0) return event.stopPropagation();
        capture(event);
        selectClip("overlay", clip.id);
        if (readOnly) return;
        gesture.current = {
          type: "track",
          id: clip.id,
          mode,
          startX: event.clientX,
          startY: event.clientY,
          clip: { ...clip },
          max: env.assets.get(clip.assetId)?.durationMs ?? Number.POSITIVE_INFINITY,
          points: snapPoints(clip.id),
          moved: false,
          drop: null,
          toMain: null,
        };
      }}
    />
  );

  const step = tickStep(pxPerMs);
  const ticks: number[] = [];
  for (let t = 0; t <= duration + 4000; t += step) ticks.push(t);

  const persons = useMemo(
    () => mainPersonClips(doc.timeline, doc.format),
    [doc.timeline, doc.format],
  );

  // Taller with more tracks, but never more than about half the window.
  const sectionHeight = Math.max(272, Math.min(520, totalHeight + 46));
  return (
    <section
      className="flex max-h-[55vh] shrink-0 flex-col border-t border-zinc-200 bg-white select-none"
      style={{ height: sectionHeight }}
    >
      <TimelineToolbar
        store={store}
        video={video}
        duration={duration}
        width={width}
        readOnly={readOnly}
      />
      {/* biome-ignore lint/a11y/noStaticElementInteractions: the right-click menu repeats actions of the properties panel and the keyboard shortcuts */}
      <div className="flex min-h-0 flex-1" onContextMenu={onContextMenu}>
        <div
          ref={labels}
          className="relative shrink-0 overflow-hidden border-r border-zinc-200 bg-zinc-50 text-[11px] text-zinc-600"
          style={{ width: HEADER_WIDTH }}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <div
            ref={labelsContent}
            className="relative will-change-transform"
            style={{ height: totalHeight }}
          >
            {rows.map((row) => (
              <TrackHeader
                key={
                  row.kind === "track"
                    ? `track-${row.info?.id ?? row.track}`
                    : row.kind === "group" || row.kind === "summary"
                      ? `${row.kind}-${row.group.id}`
                      : row.kind
                }
                row={row}
                readOnly={readOnly}
                selected={row.kind === "track" && selectedTracks.includes(row.track)}
                renaming={renaming}
                onRename={setRenaming}
                hiddenTracks={hidden}
                captionsHidden={Boolean(captionsElement?.hidden)}
                onSelect={selectTrack}
                onGrip={startRowDrag}
              />
            ))}
            {indicator?.type === "line" && gesture.current?.type === "rows" ? (
              <div
                className="pointer-events-none absolute inset-x-0 z-10 h-0.5 bg-brand-600"
                style={{ top: indicator.y - 1 }}
              />
            ) : null}
          </div>
        </div>
        <div
          ref={scroller}
          className="relative min-w-0 flex-1 overflow-x-auto overflow-y-auto"
          onScroll={(event) => {
            // The rows can scroll further than the headers (the horizontal scrollbar takes room
            // there), so the headers move by the same amount instead of scrolling themselves.
            if (labelsContent.current) {
              labelsContent.current.style.transform = `translateY(${-event.currentTarget.scrollTop}px)`;
            }
          }}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={(event) => {
            if (event.ctrlKey || event.metaKey) {
              const factor = Math.exp(-event.deltaY * 0.004);
              video.getState().setZoom(pxPerSecond * factor);
            }
          }}
        >
          <div className="relative" style={{ width: contentWidth, height: totalHeight }}>
            {/* Ruler */}
            <div
              className="absolute inset-x-0 top-0 cursor-pointer border-b border-zinc-100 bg-white"
              style={{ height: RULER }}
              onPointerDown={startScrub}
            >
              {ticks.map((t) => (
                <div
                  key={t}
                  className="absolute top-0 h-full border-l border-zinc-200 pl-1 text-[10px] leading-6 text-zinc-400 tabular-nums"
                  style={{ left: xOf(t) }}
                >
                  {step < 1000 ? formatTimecode(t) : formatTimecode(t, false)}
                </div>
              ))}
              <div
                className="absolute top-0 h-full bg-zinc-100/70"
                style={{ left: xOf(duration), right: 0 }}
                title="Nach dem Ende"
              />
            </div>

            {rows.map((row) => {
              if (row.kind === "new") {
                return (
                  <div
                    key="new"
                    role="application"
                    aria-label="Neue Spur"
                    className="absolute inset-x-0 border-b border-zinc-100"
                    style={{ top: row.top, height: row.height }}
                    onPointerDown={startScrub}
                    onDragOver={allowDrop}
                    onDrop={(event) => onDrop(event, count + 1)}
                  >
                    <span className="pointer-events-none sticky left-4 inline-flex h-full items-center text-[10px] text-zinc-400">
                      {count === 0
                        ? "Video, Clip oder Titel hierher ziehen: liegt dann auf einer eigenen Spur über dem Bild"
                        : "Hierher ziehen für eine neue Spur ganz oben – oder zwischen zwei Spuren"}
                    </span>
                  </div>
                );
              }
              if (row.kind === "captions-cta") {
                return (
                  <CaptionTrack
                    key="captions-cta"
                    top={row.top}
                    layout={layout}
                    xOf={xOf}
                    pxPerMs={pxPerMs}
                    readOnly={readOnly}
                    onScrub={startScrub}
                  />
                );
              }
              if (row.kind === "group") {
                return (
                  <div
                    key={`group-${row.group.id}`}
                    className="absolute inset-x-0 border-b border-zinc-200 bg-zinc-100/80"
                    style={{ top: row.top, height: row.height }}
                    onPointerDown={startScrub}
                  />
                );
              }
              if (row.kind === "summary") {
                return (
                  <GroupSummary
                    key={`summary-${row.group.id}`}
                    row={row}
                    doc={doc}
                    duration={duration}
                    xOf={xOf}
                    pxPerMs={pxPerMs}
                    hiddenTracks={hidden}
                    onPointerDown={(event) => {
                      if (event.button !== 0) return;
                      if (readOnly) return startScrub(event);
                      capture(event);
                      const entries = trackEntries(doc).filter((entry) =>
                        row.tracks.includes(entry.track),
                      );
                      if (entries.length === 0) return;
                      const earliest = Math.min(...entries.map((entry) => entry.start));
                      gesture.current = {
                        type: "group-shift",
                        tracks: row.tracks,
                        startX: event.clientX,
                        moved: false,
                        points: snapPoints(),
                        earliest,
                      };
                    }}
                  />
                );
              }
              if (row.kind === "person") {
                const cut =
                  row.split === 0
                    ? persons
                    : overlayClips(doc.timeline).filter(
                        (clip) => clip.track === row.split && clipCutout(clip),
                      );
                return (
                  <div
                    key="person"
                    role="application"
                    aria-label="Person (freigestellt)"
                    className="absolute inset-x-0 border-y border-amber-300 bg-amber-50"
                    style={{ top: row.top, height: row.height }}
                    onPointerDown={startScrub}
                  >
                    {cut.map((clip) => (
                      <button
                        type="button"
                        key={clip.id}
                        className="absolute top-1 bottom-1 flex items-center gap-1 overflow-hidden rounded border border-amber-300 bg-amber-100 px-1.5 text-[10px] text-amber-900 [&_svg]:size-3"
                        style={{
                          left: xOf(clip.start),
                          width: Math.max(6, (overlayClipEnd(clip) - clip.start) * pxPerMs - 1),
                        }}
                        title="Die freigestellte Person dieses Clips – was auf den Spuren darunter liegt, ist hinter ihr"
                        onPointerDown={(event) => {
                          event.stopPropagation();
                          if (event.button !== 0) return;
                          selectClip(row.split === 0 ? "video" : "overlay", clip.id);
                        }}
                      >
                        <PersonStanding />
                        <span className="truncate">
                          Person · was auf den Spuren darunter liegt, ist hinter ihr – Titel, Bilder
                          oder Videos darunter ziehen
                        </span>
                      </button>
                    ))}
                  </div>
                );
              }
              if (row.kind === "track") {
                return (
                  <div
                    key={`track-${row.info?.id ?? row.track}`}
                    role="application"
                    aria-label={trackName(row.track, row.info, row.captions)}
                    className={cn(
                      "absolute inset-x-0 border-b border-zinc-100",
                      row.clips.length > 0 ? "bg-violet-50/40" : "bg-sky-50/20",
                      row.behindPerson && "bg-amber-50/40",
                      selectedTracks.includes(row.track) && "bg-brand-50/60",
                    )}
                    style={{ top: row.top, height: row.height }}
                    onPointerDown={startScrub}
                    onDragOver={allowDrop}
                    onDrop={(event) => onDrop(event, row.track)}
                  />
                );
              }
              return null;
            })}

            {/* Items on the tracks (above the rows, so they stay clickable) */}
            {trackRows.map((row) => {
              const dimmed = hidden.has(row.track);
              return (
                <Fragment key={`items-${row.info?.id ?? row.track}`}>
                  {row.captions ? (
                    <div className={cn(dimmed && "opacity-40")}>
                      <CaptionTrack
                        top={row.top}
                        layout={layout}
                        xOf={xOf}
                        pxPerMs={pxPerMs}
                        readOnly={readOnly}
                        onScrub={startScrub}
                      />
                    </div>
                  ) : null}
                  {row.clips.map(({ item, lane }) =>
                    overlayItem(
                      item,
                      row.top + lane * row.laneHeight + 3,
                      row.laneHeight - 6,
                      dimmed,
                    ),
                  )}
                  {row.elements.map(({ item, lane }) =>
                    elementBar(item, row.top, lane, row.laneHeight, dimmed),
                  )}
                </Fragment>
              );
            })}
            {trackGhost ? (
              <div
                className="pointer-events-none absolute z-[2] rounded-md border-2 border-dashed border-brand-500 bg-brand-500/15"
                style={{
                  left: xOf(trackGhost.start),
                  width: Math.max(8, trackGhost.length * pxPerMs),
                  top:
                    (indicator?.type === "row" ? indicator.top : (indicator?.y ?? newRow.top)) + 3,
                  height: indicator?.type === "row" ? indicator.height - 6 : 20,
                }}
              />
            ) : null}
            {indicator?.type === "row" ? (
              <div
                className="pointer-events-none absolute inset-x-0 z-[3] border-y-2 border-brand-500/70 bg-brand-500/5"
                style={{ top: indicator.top, height: indicator.height }}
              />
            ) : null}
            {indicator?.type === "line" ? (
              <div
                className="pointer-events-none absolute inset-x-0 z-[3] flex items-center"
                style={{ top: indicator.y - 1, height: 2 }}
              >
                <div className="h-0.5 w-full bg-brand-600" />
                {gesture.current?.type !== "rows" ? (
                  <span className="sticky left-2 -mt-4 ml-2 rounded bg-brand-600 px-1.5 text-[10px] whitespace-nowrap text-white">
                    Neue Spur
                  </span>
                ) : null}
              </div>
            ) : null}

            {/* Main track */}
            <div
              role="application"
              aria-label="Hauptspur"
              className="absolute inset-x-0 bg-zinc-50"
              style={{ top: mainRow.top, height: VIDEO_ROW }}
              onPointerDown={startScrub}
              onDragOver={allowDrop}
              onDrop={(event) => onDrop(event, "video")}
            >
              {layout.length === 0 ? (
                <div className="absolute inset-2 flex items-center justify-center rounded-lg border-2 border-dashed border-zinc-300 text-xs text-zinc-500">
                  Videos oder Bilder aus „Medien“ hierher ziehen – oder auf die Fläche
                </div>
              ) : null}
              {layout.map((entry) => (
                <ClipItem
                  key={entry.clip.id}
                  entry={entry}
                  next={layout[entry.index + 1]}
                  asset={env.assets.get(entry.clip.assetId)}
                  pxPerMs={pxPerMs}
                  left={xOf(entry.start + entry.overlap / 2)}
                  selected={selectedClip?.track === "video" && selectedClip.id === entry.clip.id}
                  transcribing={
                    infos.get(entry.clip.assetId)?.status === "processing" ||
                    infos.get(entry.clip.assetId)?.status === "queued"
                  }
                  onPointerDown={(event) => {
                    if (event.button !== 0) return event.stopPropagation();
                    capture(event);
                    selectClip("video", entry.clip.id);
                    if (readOnly) return;
                    gesture.current = {
                      type: "clip-move",
                      clipId: entry.clip.id,
                      startX: event.clientX,
                      startY: event.clientY,
                      entry,
                      points: snapPoints(entry.clip.id),
                      moved: false,
                      insertAt: entry.index,
                      toTrack: null,
                    };
                  }}
                  onTrim={(event, edge) => {
                    if (event.button !== 0) return event.stopPropagation();
                    capture(event);
                    selectClip("video", entry.clip.id);
                    if (readOnly) return;
                    const asset = env.assets.get(entry.clip.assetId);
                    gesture.current = {
                      type: "clip-trim",
                      clipId: entry.clip.id,
                      edge,
                      startX: event.clientX,
                      entry,
                      maxSource: asset?.durationMs ?? Number.POSITIVE_INFINITY,
                      points: snapPoints(entry.clip.id),
                    };
                  }}
                />
              ))}
              {layout.slice(1).map((entry) => (
                <TransitionMarker
                  key={`cut-${entry.clip.id}`}
                  store={store}
                  video={video}
                  entry={entry}
                  x={xOf(entry.start + entry.overlap / 2)}
                  width={entry.overlap * pxPerMs}
                  readOnly={readOnly}
                  cuts={layout.length - 1}
                />
              ))}
              {dragGhost ? (
                <div
                  className="pointer-events-none absolute top-0 bottom-0 w-0.5 bg-brand-600"
                  style={{ left: dragGhost.insertX - 1 }}
                />
              ) : null}
            </div>

            {/* Music */}
            <div
              role="application"
              aria-label="Musikspur"
              className="absolute inset-x-0 border-t border-zinc-100"
              style={{ top: musicRow.top, height: musicRow.height }}
              onPointerDown={startScrub}
              onDragOver={allowDrop}
              onDrop={(event) => onDrop(event, "audio")}
            >
              {audioClips.length === 0 ? (
                <div className="absolute inset-1 flex items-center gap-2 rounded-md border border-dashed border-zinc-200 px-3 text-[11px] text-zinc-400">
                  Musik oder Sprachaufnahme hierher ziehen
                  {readOnly ? null : (
                    <button
                      type="button"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={openAudioLibrary}
                      className="rounded px-1.5 py-0.5 font-medium text-brand-700 hover:bg-brand-50"
                    >
                      Audio auswählen
                    </button>
                  )}
                </div>
              ) : null}
              {audioClips.map((clip) => (
                <AudioItem
                  key={clip.id}
                  clip={clip}
                  asset={env.assets.get(clip.assetId)}
                  pxPerMs={pxPerMs}
                  left={xOf(clip.start)}
                  lane={audioLanesMap.get(clip) ?? 0}
                  selected={selectedClip?.track === "audio" && selectedClip.id === clip.id}
                  onPointerDown={(event, mode) => {
                    if (event.button !== 0) return event.stopPropagation();
                    capture(event);
                    selectClip("audio", clip.id);
                    if (readOnly) return;
                    gesture.current = {
                      type: "audio",
                      id: clip.id,
                      mode,
                      startX: event.clientX,
                      clip: { ...clip },
                      max: env.assets.get(clip.assetId)?.durationMs ?? Number.POSITIVE_INFINITY,
                      points: snapPoints(clip.id),
                      moved: false,
                    };
                  }}
                />
              ))}
            </div>

            <PeerPlayheads xOf={(time) => PAD + time * pxPerMs} height={totalHeight} />
            <Playhead pxPerMs={pxPerMs} scroller={scroller} height={totalHeight} />
          </div>
        </div>
      </div>
      {/* Outside the scroller: React passes events from the menu's portal up the tree. */}
      <TimelineMenu
        menu={menu}
        onClose={() => setMenu(null)}
        onRenameTrack={(key) => setRenaming(key)}
      />
    </section>
  );
}

/**
 * Header of a row: grip (drag to move the track), name (double click to
 * rename), eye (hide in preview and export). Groups fold and unfold.
 */
function TrackHeader({
  row,
  readOnly,
  selected,
  renaming,
  onRename,
  hiddenTracks: hidden,
  captionsHidden,
  onSelect,
  onGrip,
}: {
  row: Row;
  readOnly: boolean;
  selected: boolean;
  renaming: string | null;
  onRename: (key: string | null) => void;
  hiddenTracks: ReadonlySet<number>;
  captionsHidden: boolean;
  onSelect: (event: ReactPointerEvent, track: number) => void;
  onGrip: (event: ReactPointerEvent, first: number, count: number) => void;
}) {
  const store = useEditorApi();
  const base =
    "absolute inset-x-0 flex items-center gap-1 overflow-hidden border-b border-zinc-100 px-1.5 whitespace-nowrap [&_svg]:size-3.5 [&_svg]:shrink-0";
  const style = { top: row.top, height: row.height };
  const iconButton =
    "rounded p-0.5 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700 disabled:opacity-40";
  if (row.kind === "new") {
    return (
      <div className={cn(base, "text-zinc-400")} style={style}>
        <button
          type="button"
          disabled={readOnly}
          onClick={() => addTrack(store)}
          className="inline-flex items-center gap-1 rounded px-1 py-0.5 font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-40 [&_svg]:size-3.5"
          title="Leere Spur ganz oben hinzufügen"
        >
          <Plus /> Spur hinzufügen
        </button>
      </div>
    );
  }
  if (row.kind === "captions-cta") {
    return (
      <div className={cn(base, "items-start pt-1.5")} style={style}>
        <Captions /> <span className="truncate">Untertitel</span>
      </div>
    );
  }
  if (row.kind === "person") {
    return (
      <div
        data-menu={`track:${row.track}`}
        className={cn(
          base,
          "border-y border-amber-300 bg-amber-100 font-medium text-amber-900",
          row.grouped && "pl-3",
        )}
        style={style}
        title="Die freigestellte Person als eigene Ebene: ziehen, um sie vor oder hinter andere Spuren zu legen"
      >
        <Grip disabled={readOnly} onPointerDown={(event) => onGrip(event, row.track, 1)} />
        <PersonStanding />{" "}
        <span className="truncate">{row.info?.name ?? "Person (freigestellt)"}</span>
      </div>
    );
  }
  if (row.kind === "main") {
    return (
      <div className={cn(base, "items-start pt-2 pl-6 font-medium text-zinc-700")} style={style}>
        <Film /> <span className="truncate">Spur 1 · Hauptspur</span>
      </div>
    );
  }
  if (row.kind === "music") {
    return (
      <div className={cn(base, "items-start pt-2 pl-6")} style={style}>
        <Music /> <span className="truncate">Musik</span>
        {readOnly ? null : (
          <button
            type="button"
            title="Musik oder Sprachaufnahme hinzufügen"
            aria-label="Musik oder Sprachaufnahme hinzufügen"
            onClick={openAudioLibrary}
            className={cn(iconButton, "ml-auto")}
          >
            <Plus />
          </button>
        )}
      </div>
    );
  }
  if (row.kind === "summary") {
    return (
      <div className={cn(base, "bg-zinc-100/60 pl-6 text-[10px] text-zinc-500")} style={style}>
        {row.tracks.length} {row.tracks.length === 1 ? "Spur" : "Spuren"} · zum Verschieben seitlich
        ziehen
      </div>
    );
  }
  if (row.kind === "group") {
    const key = `group:${row.group.id}`;
    const collapsed = Boolean(row.group.collapsed);
    return (
      <div
        data-menu={key}
        className={cn(base, "border-zinc-200 bg-zinc-100 font-medium text-zinc-700")}
        style={style}
      >
        <Grip
          disabled={readOnly}
          onPointerDown={(event) => onGrip(event, Math.min(...row.tracks), row.tracks.length)}
        />
        <button
          type="button"
          className={iconButton}
          title={collapsed ? "Gruppe aufklappen" : "Gruppe zuklappen"}
          aria-label={collapsed ? "Gruppe aufklappen" : "Gruppe zuklappen"}
          disabled={readOnly}
          onClick={() => setGroupCollapsed(store, row.group.id, !collapsed)}
        >
          {collapsed ? <ChevronRight /> : <ChevronDown />}
        </button>
        <FolderOpen />
        <EditableName
          value={row.group.name}
          editing={renaming === key}
          onStart={() => !readOnly && onRename(key)}
          onDone={(name) => {
            onRename(null);
            if (name !== null) renameGroup(store, row.group.id, name);
          }}
        />
        <button
          type="button"
          className={cn(iconButton, "ml-auto")}
          disabled={readOnly}
          title={row.hidden ? "Gruppe zeigen" : "Gruppe ausblenden"}
          aria-label={row.hidden ? "Gruppe zeigen" : "Gruppe ausblenden"}
          onClick={() => toggleGroupHidden(store, row.group.id)}
        >
          {row.hidden ? <EyeOff /> : <Eye />}
        </button>
      </div>
    );
  }
  // A track.
  const key = `track:${row.track}`;
  const isHidden = hidden.has(row.track);
  const name = trackName(row.track, row.info, row.captions);
  return (
    <div
      data-menu={key}
      className={cn(
        base,
        row.laneHeight === CLIP_LANE && !row.captions ? "items-start pt-1.5" : null,
        row.grouped && "pl-3",
        row.behindPerson && "border-l-2 border-l-amber-400",
        selected ? "bg-brand-100/70 text-brand-900" : "hover:bg-zinc-100",
        isHidden && "text-zinc-400",
      )}
      title={row.behindPerson ? "Liegt hinter der freigestellten Person" : undefined}
      style={style}
      onPointerDown={(event) => {
        if (event.button === 0) onSelect(event, row.track);
      }}
    >
      <Grip disabled={readOnly} onPointerDown={(event) => onGrip(event, row.track, 1)} />
      {row.captions ? <Captions /> : row.clips.length > 0 ? <Layers /> : <Type />}
      <EditableName
        value={name}
        editing={renaming === key}
        onStart={() => !readOnly && onRename(key)}
        onDone={(value) => {
          onRename(null);
          if (value !== null)
            setTrackName(store, row.track, value === `Spur ${row.track + 1}` ? "" : value);
        }}
      />
      <button
        type="button"
        className={cn(iconButton, "ml-auto")}
        disabled={readOnly}
        title={
          row.captions
            ? captionsHidden
              ? "Untertitel im Video zeigen"
              : "Untertitel im Video ausblenden"
            : isHidden
              ? "Spur zeigen"
              : "Spur ausblenden (Vorschau und Export)"
        }
        onPointerDown={(event) => event.stopPropagation()}
        onClick={() =>
          row.captions
            ? setCaptionsHidden(store, !captionsHidden)
            : toggleTrackHidden(store, row.track)
        }
      >
        {(row.captions ? captionsHidden : isHidden) ? <EyeOff /> : <Eye />}
      </button>
    </div>
  );
}

function Grip({
  disabled,
  onPointerDown,
}: {
  disabled: boolean;
  onPointerDown: (event: ReactPointerEvent) => void;
}) {
  return (
    <span
      className={cn(
        "-ml-0.5 flex h-5 w-3 shrink-0 items-center justify-center text-zinc-300 [&_svg]:size-3",
        disabled ? "opacity-0" : "cursor-grab hover:text-zinc-600 active:cursor-grabbing",
      )}
      title="Ziehen, um die Spur nach oben oder unten zu verschieben"
      onPointerDown={disabled ? undefined : onPointerDown}
    >
      <GripVertical />
    </span>
  );
}

/** A name that turns into a text field on double click (Enter saves, Escape cancels). */
function EditableName({
  value,
  editing,
  onStart,
  onDone,
}: {
  value: string;
  editing: boolean;
  onStart: () => void;
  onDone: (value: string | null) => void;
}) {
  if (editing) {
    return (
      <input
        // biome-ignore lint/a11y/noAutofocus: opened by a double click on the name
        autoFocus
        defaultValue={value}
        maxLength={80}
        className="h-5 min-w-0 flex-1 rounded border border-brand-400 bg-white px-1 text-[11px] text-zinc-900 outline-none"
        onPointerDown={(event) => event.stopPropagation()}
        onFocus={(event) => event.currentTarget.select()}
        onBlur={(event) => onDone(event.currentTarget.value)}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Enter") event.currentTarget.blur();
          if (event.key === "Escape") onDone(null);
        }}
      />
    );
  }
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: renaming is also in the track's menu
    <span
      className="min-w-0 truncate"
      onDoubleClick={onStart}
      title={`${value} – Doppelklick zum Umbenennen`}
    >
      {value}
    </span>
  );
}

/** A folded group: all its tracks as thin bars; dragging it sideways moves everything in time. */
function GroupSummary({
  row,
  doc,
  duration,
  xOf,
  pxPerMs,
  hiddenTracks: hidden,
  onPointerDown,
}: {
  row: Extract<Row, { kind: "summary" }>;
  doc: SceneDocument;
  duration: number;
  xOf: (ms: number) => number;
  pxPerMs: number;
  hiddenTracks: ReadonlySet<number>;
  onPointerDown: (event: ReactPointerEvent) => void;
}) {
  const high = Math.max(...row.tracks);
  const entries = trackEntries(doc).filter((entry) => row.tracks.includes(entry.track));
  return (
    <div
      role="application"
      aria-label={`Gruppe ${row.group.name}`}
      className="absolute inset-x-0 cursor-ew-resize border-b border-zinc-200 bg-zinc-100/40"
      style={{ top: row.top, height: row.height }}
      onPointerDown={onPointerDown}
      title="Seitlich ziehen: alles in der Gruppe gemeinsam verschieben"
    >
      {entries.map((entry) => (
        <div
          key={entry.id}
          className={cn(
            "pointer-events-none absolute rounded-sm",
            entry.kind === "clip" ? "bg-violet-400" : "bg-sky-400",
            hidden.has(entry.track) && "opacity-40",
          )}
          style={{
            left: xOf(entry.start),
            width: Math.max(4, (Math.min(entry.end, duration) - entry.start) * pxPerMs),
            top: 4 + (high - entry.track) * SUMMARY_LANE,
            height: SUMMARY_LANE - 2,
          }}
        />
      ))}
    </div>
  );
}

/**
 * The caption track: where the captions appear, and until then the state of
 * the recognition per clip. Recognised speech shows faintly before captions
 * are added; a click on a caption opens it in the inspector.
 */
function CaptionTrack({
  top,
  layout,
  xOf,
  pxPerMs,
  readOnly,
  onScrub,
}: {
  top: number;
  layout: ClipLayout[];
  xOf: (ms: number) => number;
  pxPerMs: number;
  readOnly: boolean;
  onScrub: (event: ReactPointerEvent) => void;
}) {
  const store = useEditorApi();
  const video = useVideoApi()!;
  const env = useEnv();
  const selection = useEditor((s) => s.selection);
  const { pages, rows, captionsElement, infos } = useCaptions();
  const activePage = useVideo((s) => captionPageAt(pages, s.time)?.index ?? -1);
  const rowOf = useMemo(() => new Map(rows.map((row) => [row.page, row])), [rows]);
  const selected = captionsElement ? selection.includes(captionsElement.id) : false;

  // Clips whose captions are not there yet: preparing, waiting, recognising, failed.
  const states = layout.flatMap((entry) => {
    const { clip } = entry;
    if (clip.kind !== "video" || clip.muted || clip.volume === 0) return [];
    const asset = env.assets.get(clip.assetId);
    if (asset?.media && !asset.media.hasAudio) return [];
    const info = infos.get(clip.assetId);
    if (info === undefined || info?.status === "done") return [];
    return [
      {
        entry,
        info,
        preparing: asset?.media?.status === "processing",
      },
    ];
  });

  const selectCaptions = () => {
    if (!captionsElement) return;
    video.getState().selectClip(null);
    store.getState().select([captionsElement.id]);
  };
  const cta = !captionsElement && !readOnly && layout.some((entry) => entry.clip.kind === "video");
  return (
    <div
      data-menu="captions"
      className="absolute inset-x-0 border-y border-zinc-100 bg-violet-50/40"
      style={{ top, height: CAPTIONS_ROW }}
      onPointerDown={onScrub}
    >
      {pages.map((page) => {
        const row = rowOf.get(page);
        const text = captionText(page);
        const style = {
          left: xOf(page.start),
          width: Math.max(4, (page.end - page.start) * pxPerMs - 1),
        };
        if (!captionsElement) {
          return (
            <div
              key={`${page.index}-${page.start}`}
              className="pointer-events-none absolute top-1 bottom-1 overflow-hidden rounded border border-dashed border-violet-300 px-1 text-[10px] leading-[16px] whitespace-nowrap text-violet-700/60"
              style={style}
              title={text}
            >
              {text.replace(/\n/g, " ")}
            </div>
          );
        }
        return (
          <button
            type="button"
            key={`${page.index}-${page.start}`}
            className={cn(
              "absolute top-1 bottom-1 overflow-hidden rounded border px-1 text-left text-[10px] leading-[18px] whitespace-nowrap",
              page.index === activePage
                ? "border-violet-500 bg-violet-200 text-violet-950"
                : "border-violet-300 bg-violet-100 text-violet-900 hover:bg-violet-200/70",
              selected && "border-violet-500",
              captionsElement.hidden && "opacity-40",
            )}
            style={style}
            onPointerDown={(event) => {
              event.stopPropagation();
              if (event.button !== 0) return;
              selectCaptions();
              video.getState().seek(page.start);
              if (row && video.getState().captionTab === "text") {
                video.getState().focusCaption(row.key);
              }
            }}
            onDoubleClick={() => {
              if (row) video.getState().focusCaption(row.key, !readOnly);
            }}
            title={`${text}\nDoppelklick: Text korrigieren`}
          >
            {text.replace(/\n/g, " ")}
          </button>
        );
      })}
      {states.map(({ entry, info, preparing }) => (
        <button
          type="button"
          key={`state-${entry.clip.id}`}
          className={cn(
            "absolute top-1 bottom-1 flex items-center gap-1 overflow-hidden rounded border px-1.5 text-left text-[10px] whitespace-nowrap [&_svg]:size-3",
            info?.status === "failed"
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-violet-200 bg-white/80 text-violet-800",
          )}
          style={{
            left: xOf(entry.start) + 1,
            width: Math.max(4, (entry.end - entry.start) * pxPerMs - 2),
          }}
          title="Details in den Clip-Eigenschaften"
          onPointerDown={(event) => {
            event.stopPropagation();
            if (event.button !== 0) return;
            store.getState().select([]);
            video.getState().selectClip({ track: "video", id: entry.clip.id });
          }}
        >
          {info?.status === "queued" || info?.status === "processing" ? (
            <TranscribingLabel info={info} />
          ) : info?.status === "failed" ? (
            <>
              <AlertTriangle />
              <span className="truncate">Spracherkennung fehlgeschlagen</span>
            </>
          ) : preparing ? (
            <>
              <Loader2 className="animate-spin" />
              <span className="truncate">Video wird vorbereitet …</span>
            </>
          ) : (
            <span className="truncate">Sprache noch nicht erkannt</span>
          )}
        </button>
      ))}
      {cta ? (
        <button
          type="button"
          className="sticky left-2 z-[3] mt-1 inline-flex h-5 items-center gap-1 rounded bg-violet-600 px-2 text-[11px] font-medium text-white shadow-sm hover:bg-violet-700"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => openCaptions(store, video)}
        >
          <Captions className="size-3" /> Untertitel einblenden
        </button>
      ) : null}
      {captionsElement && pages.length === 0 && states.length === 0 ? (
        <span className="pointer-events-none absolute top-1.5 left-4 text-[11px] text-violet-700/70">
          Noch keine gesprochenen Wörter
        </span>
      ) : null}
    </div>
  );
}

function Handle({
  side,
  onPointerDown,
}: {
  side: "left" | "right";
  onPointerDown: (event: ReactPointerEvent) => void;
}) {
  return (
    <span
      className={cn(
        "absolute top-0 bottom-0 z-10 w-2 cursor-ew-resize bg-black/0 hover:bg-black/15",
        side === "left" ? "left-0 rounded-l-md" : "right-0 rounded-r-md",
      )}
      onPointerDown={(event) => {
        event.stopPropagation();
        onPointerDown(event);
      }}
    />
  );
}

function Filmstrip({
  asset,
  sourceIn,
  sourceOut,
  width,
  height,
}: {
  asset: AssetInfo | undefined;
  sourceIn: number;
  sourceOut: number;
  width: number;
  height: number;
}) {
  const strip = asset?.media?.filmstrip;
  const url = asset?.media?.filmstripUrl;
  if (asset?.kind === "image") {
    return (
      <div
        className="absolute inset-0 bg-repeat-x"
        style={{
          backgroundImage: `url("${asset.thumbnailUrl ?? asset.url}")`,
          backgroundSize: "auto 100%",
        }}
      />
    );
  }
  if (!strip || !url) {
    return asset?.thumbnailUrl ? (
      <div
        className="absolute inset-0 bg-repeat-x opacity-80"
        style={{ backgroundImage: `url("${asset.thumbnailUrl}")`, backgroundSize: "auto 100%" }}
      />
    ) : null;
  }
  const scale = height / strip.frameHeight;
  const tileWidth = strip.frameWidth * scale;
  const count = Math.max(1, Math.ceil(width / tileWidth));
  const tiles: ReactNode[] = [];
  for (let index = 0; index < count; index++) {
    const t = sourceIn + ((index + 0.5) / count) * (sourceOut - sourceIn);
    const frame = Math.max(0, Math.min(strip.frames - 1, Math.floor(t / strip.interval)));
    tiles.push(
      <div
        key={index}
        className="absolute top-0"
        style={{
          left: index * tileWidth,
          width: tileWidth,
          height,
          backgroundImage: `url("${url}")`,
          backgroundSize: `${strip.frames * tileWidth}px ${height}px`,
          backgroundPosition: `${-frame * tileWidth}px 0`,
        }}
      />,
    );
  }
  return <div className="absolute inset-0 overflow-hidden">{tiles}</div>;
}

function WaveformStrip({
  url,
  sourceIn,
  sourceOut,
  width,
  height,
  className,
}: {
  url: string | null | undefined;
  sourceIn: number;
  sourceOut: number;
  width: number;
  height: number;
  className?: string;
}) {
  const waveform = useWaveform(url);
  const canvas = useRef<HTMLCanvasElement>(null);
  const pixelWidth = Math.max(1, Math.min(4000, Math.round(width)));
  useEffect(() => {
    const element = canvas.current;
    const data = waveform.data;
    if (!element || !data) return;
    const ratio = window.devicePixelRatio || 1;
    element.width = pixelWidth * ratio;
    element.height = height * ratio;
    const ctx = element.getContext("2d");
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.clearRect(0, 0, pixelWidth, height);
    ctx.fillStyle = getComputedStyle(element).color;
    const perPixel = (sourceOut - sourceIn) / pixelWidth;
    for (let x = 0; x < pixelWidth; x++) {
      const from = Math.floor(((sourceIn + x * perPixel) / 1000) * data.rate);
      const to = Math.max(
        from + 1,
        Math.floor(((sourceIn + (x + 1) * perPixel) / 1000) * data.rate),
      );
      let peak = 0;
      for (let index = from; index < to && index < data.peaks.length; index++) {
        peak = Math.max(peak, data.peaks[index] ?? 0);
      }
      const bar = Math.max(1, (peak / 100) * height);
      ctx.fillRect(x, (height - bar) / 2, 1, bar);
    }
  }, [waveform.data, sourceIn, sourceOut, pixelWidth, height]);
  if (!url) return null;
  return (
    <canvas
      ref={canvas}
      className={cn("pointer-events-none absolute left-0", className)}
      style={{ width: pixelWidth, height }}
    />
  );
}

function ClipItem({
  entry,
  next,
  asset,
  pxPerMs,
  left,
  selected,
  transcribing,
  onPointerDown,
  onTrim,
}: {
  entry: ClipLayout;
  /** The clip after it: with a transition, the two bars meet in its middle. */
  next: ClipLayout | undefined;
  asset: AssetInfo | undefined;
  pxPerMs: number;
  left: number;
  selected: boolean;
  transcribing: boolean;
  onPointerDown: (event: ReactPointerEvent) => void;
  onTrim: (event: ReactPointerEvent, edge: "in" | "out") => void;
}) {
  const { clip } = entry;
  const from = entry.start + entry.overlap / 2;
  const to = next ? next.start + next.overlap / 2 : entry.end;
  const width = Math.max(4, (to - from) * pxPerMs - 2);
  const height = VIDEO_ROW - 8;
  const visibleIn = clip.sourceIn + (from - entry.start) * clip.speed;
  const visibleOut = clip.sourceIn + (to - entry.start) * clip.speed;
  const peer = usePeerOnClip("video", entry.clip.id);
  return (
    <div
      data-menu={`clip:${clip.id}`}
      className={cn(
        "absolute top-1 overflow-hidden rounded-md border-2 bg-zinc-800",
        selected ? "border-brand-500" : "border-zinc-300 hover:border-zinc-400",
      )}
      style={{ left, width, height }}
      onPointerDown={onPointerDown}
      title={`${clip.name ?? asset?.name ?? "Clip"} · ${formatTimecode(entry.end - entry.start)}`}
    >
      <PeerClipMark peer={peer} />
      <Filmstrip
        asset={asset}
        sourceIn={visibleIn}
        sourceOut={visibleOut}
        width={width}
        height={height - 4}
      />
      {asset?.media?.hasAudio && clip.kind === "video" ? (
        <WaveformStrip
          url={asset.media.waveformUrl}
          sourceIn={visibleIn}
          sourceOut={visibleOut}
          width={width}
          height={14}
          className="bottom-0 bg-black/35 text-white/80"
        />
      ) : null}
      <div className="absolute top-0.5 left-2 flex max-w-[calc(100%-16px)] items-center gap-1 rounded bg-black/55 px-1 text-[10px] text-white [&_svg]:size-3">
        {clip.kind === "image" ? <ImageIcon /> : <Film />}
        <span className="truncate">{formatTimecode(entry.end - entry.start)}</span>
        {clip.speed !== 1 ? <span className="font-semibold">{clip.speed}×</span> : null}
        {clip.muted ? <VolumeX /> : null}
        <PersonMark clip={clip} asset={asset} />
        {transcribing ? <AudioLines className="animate-pulse" /> : null}
      </div>
      {asset?.media?.status === "processing" ? (
        <div className="absolute right-1 bottom-4 rounded bg-amber-500 px-1 text-[9px] font-medium text-white">
          Vorschau …
        </div>
      ) : null}
      {!asset ? (
        <div className="absolute inset-0 flex items-center justify-center bg-red-100 text-[10px] text-red-700">
          Datei fehlt
        </div>
      ) : null}
      <Handle side="left" onPointerDown={(event) => onTrim(event, "in")} />
      <Handle side="right" onPointerDown={(event) => onTrim(event, "out")} />
    </div>
  );
}

function TrackItem({
  clip,
  asset,
  pxPerMs,
  left,
  top,
  height,
  selected,
  dimmed,
  onPointerDown,
}: {
  clip: OverlayClip;
  asset: AssetInfo | undefined;
  pxPerMs: number;
  left: number;
  top: number;
  height: number;
  selected: boolean;
  dimmed?: boolean;
  onPointerDown: (event: ReactPointerEvent, mode: "move" | "start" | "end") => void;
}) {
  const width = Math.max(4, (overlayClipEnd(clip) - clip.start) * pxPerMs - 1);
  const frame = useEditor((s) => s.doc.format);
  const full = clip.box.x <= 0 && clip.box.y <= 0 && clip.opacity >= 1 && clip.radius === 0;
  // A large area is part of a split screen, a small one a picture in picture.
  const share = (clip.box.width * clip.box.height) / (frame.width * frame.height);
  const kind = full ? "" : share >= 0.2 ? "Splitscreen · " : "Bild im Bild · ";
  const peer = usePeerOnClip("overlay", clip.id);
  return (
    <div
      data-menu={`overlay:${clip.id}`}
      className={cn(
        "absolute z-[1] overflow-hidden rounded-md border-2 bg-zinc-800",
        selected ? "border-brand-500" : "border-violet-300 hover:border-violet-400",
        dimmed && "opacity-40",
      )}
      style={{ left, width, top, height }}
      onPointerDown={(event) => onPointerDown(event, "move")}
      title={`${clip.name ?? asset?.name ?? "Clip"} · ${formatTimecode(overlayClipEnd(clip) - clip.start)}`}
    >
      <PeerClipMark peer={peer} />
      <Filmstrip
        asset={asset}
        sourceIn={clip.sourceIn}
        sourceOut={clip.sourceOut}
        width={width}
        height={height - 4}
      />
      <div className="absolute top-0.5 left-2 flex max-w-[calc(100%-16px)] items-center gap-1 rounded bg-black/55 px-1 text-[10px] text-white [&_svg]:size-3">
        {clip.kind === "image" ? <ImageIcon /> : <Layers />}
        <span className="truncate">
          {kind}
          {formatTimecode(overlayClipEnd(clip) - clip.start)}
        </span>
        {clip.muted || clip.kind === "image" ? null : <AudioLines />}
        <PersonMark clip={clip} asset={asset} />
      </div>
      {!asset ? (
        <div className="absolute inset-0 flex items-center justify-center bg-red-100 text-[10px] text-red-700">
          Datei fehlt
        </div>
      ) : null}
      <Handle side="left" onPointerDown={(event) => onPointerDown(event, "start")} />
      <Handle side="right" onPointerDown={(event) => onPointerDown(event, "end")} />
    </div>
  );
}

/** A clip with a cut-out person: the icon, pulsing (with progress) while the server works. */
function PersonMark({
  clip,
  asset,
}: {
  clip: VideoClip | OverlayClip;
  asset: AssetInfo | undefined;
}) {
  if (!clipCutout(clip)) return null;
  const matte = asset?.matte;
  const busy = matte?.status === "processing";
  const failed = !matte || matte.status === "failed";
  return (
    <span
      className={cn("inline-flex items-center", failed && "text-red-300")}
      title={
        busy
          ? "Person wird freigestellt …"
          : failed
            ? "Freistellen fehlgeschlagen"
            : clip.cutout?.background === "remove"
              ? "Nur die Person (ohne Hintergrund)"
              : "Person freigestellt"
      }
    >
      <PersonStanding className={busy ? "animate-pulse" : undefined} />
      {busy && matte.progress ? <span>{Math.floor(matte.progress)} %</span> : null}
    </span>
  );
}

/**
 * The cut between two main track clips: a small button for a hard cut, a
 * bar over the overlap when there is a transition. Opens the transitions.
 */
function TransitionMarker({
  store,
  video,
  entry,
  x,
  width,
  readOnly,
  cuts,
}: {
  store: EditorStore;
  video: VideoStore;
  entry: ClipLayout;
  x: number;
  width: number;
  readOnly: boolean;
  cuts: number;
}) {
  const transition = entry.clip.transition;
  const size = Math.max(18, width);
  const duration = transition?.duration ?? DEFAULT_TRANSITION_MS;
  const shortened = transition && entry.overlap < transition.duration - 1;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={readOnly}
          data-menu={`clip:${entry.clip.id}`}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => video.getState().seek(Math.max(0, entry.start - 300))}
          title={
            transition
              ? `${TRANSITION_LABELS[transition.kind]} · ${(entry.overlap / 1000).toLocaleString("de-DE")} s`
              : "Übergang hinzufügen"
          }
          className={cn(
            "absolute top-1/2 z-10 flex h-[18px] -translate-y-1/2 items-center justify-center rounded-full border text-[10px] shadow-sm [&_svg]:size-3",
            transition
              ? "border-brand-500 bg-brand-600 text-white"
              : "border-zinc-300 bg-white text-zinc-500 opacity-70 hover:opacity-100",
          )}
          style={{ left: x - size / 2, width: size }}
        >
          {transition ? <Blend /> : <Plus />}
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="center" className="w-80">
        {/* React passes events from the portal up to the track, which would start scrubbing. */}
        <div className="space-y-3" onPointerDown={(event) => event.stopPropagation()}>
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-zinc-900">Übergang</p>
            <span className="text-[11px] text-zinc-500">zwischen zwei Clips</span>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <TransitionOption
              label="Harter Schnitt"
              active={!transition}
              onClick={() => applyTransition(store, entry.clip.id, null)}
            />
            {TRANSITION_KINDS.map((kind) => (
              <TransitionOption
                key={kind}
                label={TRANSITION_LABELS[kind]}
                active={transition?.kind === kind}
                onClick={() => applyTransition(store, entry.clip.id, { kind, duration })}
              />
            ))}
          </div>
          {transition ? (
            <>
              <label className="block space-y-1 text-xs text-zinc-600">
                Dauer: {(transition.duration / 1000).toLocaleString("de-DE")} s
                {shortened
                  ? ` (gekürzt auf ${(entry.overlap / 1000).toLocaleString("de-DE", { maximumFractionDigits: 2 })} s, die Clips sind zu kurz)`
                  : ""}
                <Slider
                  value={transition.duration}
                  min={100}
                  max={2000}
                  step={50}
                  onChange={(value) =>
                    applyTransition(
                      store,
                      entry.clip.id,
                      { kind: transition.kind, duration: value },
                      "duration",
                    )
                  }
                />
              </label>
              {cuts > 1 ? (
                <button
                  type="button"
                  className="h-8 w-full rounded-md border border-zinc-200 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
                  onClick={() => {
                    const count = applyTransitionEverywhere(
                      store,
                      transition.kind,
                      transition.duration,
                    );
                    toast.success(`${TRANSITION_LABELS[transition.kind]} an ${count} Schnitten`);
                  }}
                >
                  Für alle {cuts} Schnitte übernehmen
                </button>
              ) : null}
            </>
          ) : (
            <p className="text-xs text-zinc-500">
              Die Clips überlappen um die Dauer des Übergangs, das Video wird dadurch etwas kürzer.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TransitionOption({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-12 items-center justify-center rounded-md border px-1.5 text-center text-[11px] leading-tight",
        active
          ? "border-brand-500 bg-brand-50 font-medium text-brand-700 ring-1 ring-brand-500"
          : "border-zinc-200 text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50",
      )}
    >
      {label}
    </button>
  );
}

function AudioItem({
  clip,
  asset,
  pxPerMs,
  left,
  lane,
  selected,
  onPointerDown,
}: {
  clip: AudioClip;
  asset: AssetInfo | undefined;
  pxPerMs: number;
  left: number;
  lane: number;
  selected: boolean;
  onPointerDown: (event: ReactPointerEvent, mode: "move" | "start" | "end") => void;
}) {
  const width = Math.max(4, (clip.sourceOut - clip.sourceIn) * pxPerMs);
  const height = AUDIO_LANE - 6;
  const peer = usePeerOnClip("audio", clip.id);
  return (
    <div
      data-menu={`audio:${clip.id}`}
      className={cn(
        "absolute overflow-hidden rounded-md border bg-emerald-100 text-emerald-700",
        selected ? "border-brand-500 ring-1 ring-brand-500" : "border-emerald-300",
      )}
      style={{ left, width, top: lane * AUDIO_LANE + 3, height }}
      onPointerDown={(event) => onPointerDown(event, "move")}
      title={`${clip.name ?? asset?.name ?? "Audio"} · ${formatTimecode(clip.sourceOut - clip.sourceIn)}`}
    >
      <PeerClipMark peer={peer} />
      <WaveformStrip
        url={asset?.media?.waveformUrl}
        sourceIn={clip.sourceIn}
        sourceOut={clip.sourceOut}
        width={width}
        height={height - 2}
        className="top-0 text-emerald-500/70"
      />
      <span className="absolute top-0.5 left-2 flex items-center gap-1 truncate text-[10px] font-medium text-emerald-900 [&_svg]:size-3">
        <Music />
        {clip.name ?? asset?.name ?? "Audio"}
        {clip.ducking ? " · Ducking" : ""}
      </span>
      <Handle side="left" onPointerDown={(event) => onPointerDown(event, "start")} />
      <Handle side="right" onPointerDown={(event) => onPointerDown(event, "end")} />
    </div>
  );
}

function Playhead({
  pxPerMs,
  scroller,
  height,
}: {
  pxPerMs: number;
  scroller: React.RefObject<HTMLDivElement | null>;
  height: number;
}) {
  const time = useVideo((s) => s.time);
  const playing = useVideo((s) => s.playing);
  const seekVersion = useVideo((s) => s.seekVersion);
  const x = PAD + time * pxPerMs;
  // Keep the playhead in view while playing, and after a jump from elsewhere (caption list).
  // biome-ignore lint/correctness/useExhaustiveDependencies: also after every seek
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    const visibleLeft = element.scrollLeft;
    const visibleRight = visibleLeft + element.clientWidth;
    const outside = x > visibleRight - 40 || x < visibleLeft;
    if (playing ? outside : x > visibleRight || x < visibleLeft) element.scrollLeft = x - 80;
  }, [x, playing, scroller, seekVersion]);
  return (
    <div className="pointer-events-none absolute top-0 z-20" style={{ left: x - 1, height }}>
      <div className="absolute -left-[5px] top-0 size-3 rotate-45 rounded-[2px] bg-rose-500" />
      <div className="h-full w-0.5 bg-rose-500" />
    </div>
  );
}

function TimelineToolbar({
  store,
  video,
  duration,
  width,
  readOnly,
}: {
  store: EditorStore;
  video: VideoStore;
  duration: number;
  width: number;
  readOnly: boolean;
}) {
  const playing = useVideo((s) => s.playing);
  const time = useVideo((s) => s.time);
  const pxPerSecond = useVideo((s) => s.pxPerSecond);
  const selectedClip = useVideo((s) => s.selectedClip);
  const framing = useVideo((s) => s.framing);
  const safeZones = useVideo((s) => s.safeZones);
  const frameWidth = useEditor((s) => s.doc.format.width);
  const frameHeight = useEditor((s) => s.doc.format.height);
  const safeZoneSet = videoSafeZones({ width: frameWidth, height: frameHeight });
  const { words, transcripts } = useCaptions();
  const [pause, setPause] = useState(700);
  return (
    <div className="flex h-11 shrink-0 items-center gap-1 overflow-hidden border-b border-zinc-100 px-2 whitespace-nowrap">
      <IconButton label="Zum Anfang" shortcut="Home" onClick={() => video.getState().seek(0)}>
        <SkipBack />
      </IconButton>
      <button
        type="button"
        onClick={() => video.getState().toggle()}
        title={playing ? "Pause (Leertaste)" : "Abspielen (Leertaste)"}
        className="inline-flex size-8 items-center justify-center rounded-full bg-zinc-900 text-white hover:bg-zinc-700 [&_svg]:size-4"
      >
        {playing ? <Pause /> : <Play className="translate-x-px" />}
      </button>
      <IconButton label="Zum Ende" shortcut="End" onClick={() => video.getState().seek(duration)}>
        <SkipForward />
      </IconButton>
      <span className="ml-1 shrink-0 text-xs text-zinc-600 tabular-nums">
        {formatTimecode(time)} / {formatTimecode(duration)}
      </span>
      <div className="mx-1 h-5 w-px bg-zinc-200" />
      <IconButton
        label="Teilen am Abspielkopf"
        shortcut="S"
        disabled={readOnly}
        onClick={() => splitAtPlayhead(store, video)}
      >
        <Scissors />
      </IconButton>
      <IconButton
        label="Clip duplizieren"
        disabled={readOnly || selectedClip?.track !== "video"}
        onClick={() => duplicateSelectedClip(store, video)}
      >
        <Copy />
      </IconButton>
      <IconButton
        label="Clip löschen (Lücke schließt sich)"
        shortcut="⌫"
        disabled={readOnly || !selectedClip}
        onClick={() => deleteSelectedClip(store, video)}
      >
        <Trash2 />
      </IconButton>
      <IconButton
        label="Bildausschnitt auf der Fläche verschieben"
        active={framing}
        disabled={readOnly || selectedClip?.track !== "video"}
        onClick={() => video.getState().setFraming(!framing)}
      >
        <Crop />
      </IconButton>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={readOnly || words.length === 0}
            title={
              words.length === 0
                ? "Braucht die Spracherkennung"
                : "Pausen automatisch herausschneiden"
            }
            className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs text-zinc-700 hover:bg-zinc-100 disabled:opacity-40 [&_svg]:size-4"
          >
            <Wand2 /> <span className="hidden xl:inline">Stille entfernen</span>
          </button>
        </PopoverTrigger>
        <PopoverContent side="top" align="start" className="w-72 space-y-3">
          <p className="text-sm font-medium text-zinc-900">Stille entfernen</p>
          <p className="text-xs text-zinc-500">
            Schneidet Pausen zwischen gesprochenen Wörtern heraus. Rückgängig mit ⌘Z.
          </p>
          <label className="block space-y-1 text-xs text-zinc-600">
            Pausen länger als {(pause / 1000).toLocaleString("de-DE")} s
            <Slider value={pause} min={300} max={2500} step={100} onChange={setPause} />
          </label>
          <button
            type="button"
            className="h-8 w-full rounded-md bg-brand-600 text-sm font-medium text-white hover:bg-brand-700"
            onClick={() => {
              const removed = removeSilences(store, transcripts, pause);
              if (removed > 0) {
                video.getState().seek(0);
                toast.success(
                  `${(removed / 1000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} s Stille entfernt`,
                );
              } else toast.info("Keine so langen Pausen gefunden");
            }}
          >
            Pausen entfernen
          </button>
        </PopoverContent>
      </Popover>
      {safeZoneSet ? (
        <IconButton
          label={`Safe Zones zeigen – ${safeZoneSet.title}: ${safeZoneSet.description}`}
          active={safeZones}
          onClick={() => video.getState().toggleSafeZones()}
        >
          <ScanLine />
        </IconButton>
      ) : (
        // A disabled button gets no pointer events: the wrapper explains why.
        <Tip label={NO_SAFE_ZONES_HINT}>
          <span className="inline-flex">
            <IconButton label="Safe Zones (in diesem Format keine)" disabled>
              <ScanLine />
            </IconButton>
          </span>
        </Tip>
      )}
      <div className="flex-1" />
      <IconButton label="Herauszoomen" onClick={() => video.getState().setZoom(pxPerSecond / 1.4)}>
        <Minus />
      </IconButton>
      <Slider
        className="w-24"
        value={Math.log(pxPerSecond)}
        min={Math.log(4)}
        max={Math.log(600)}
        step={0.01}
        onChange={(value) => video.getState().setZoom(Math.exp(value))}
      />
      <IconButton label="Hineinzoomen" onClick={() => video.getState().setZoom(pxPerSecond * 1.4)}>
        <Plus />
      </IconButton>
      <IconButton
        label="Ganzes Video zeigen"
        onClick={() =>
          video.getState().setZoom(((width - PAD * 2) / Math.max(duration, 1000)) * 1000)
        }
      >
        <Maximize2 />
      </IconButton>
    </div>
  );
}
